import Phaser from "phaser";
import { CHARACTERS, GAME_SOUND_KEYS, type GameSoundKey } from "@spieleparadies/shared";

import {
  RETTE_ANNA_ASSETS,
  characterAssetEntries,
  characterSpriteKey,
} from "../assets";
import type { MountContext } from "../../registry";
import { useCustomSoundsStore } from "../../../state/useCustomSoundsStore";
import { unlockAudioContexts } from "../../../lib/audioUnlock";

interface BootSceneData {
  ctx: MountContext;
}

/**
 * Lädt Assets, baut Platzhalter-Texturen falls Dateien fehlen, und springt
 * dann in die PlayScene.
 */
export class BootScene extends Phaser.Scene {
  private ctx!: MountContext;

  constructor() {
    super("BootScene");
  }

  init(data: BootSceneData) {
    this.ctx = data.ctx;
  }

  preload() {
    const loader = this.load;

    // Bilder laden – wenn die Datei nicht existiert, fängt der `loaderror`
    // unten den Fehler ab und wir generieren ein Platzhalter-Bild.
    for (const [key, path] of Object.entries(RETTE_ANNA_ASSETS.images)) {
      loader.image(key, path);
    }
    // Audio: pro Slot prüfen, ob der Raum eine eigene Aufnahme hinterlegt
    // hat. Wenn ja, diese Data-URL laden – sonst die Default-Datei.
    const customSounds = useCustomSoundsStore.getState().sounds;
    const customKeys = new Set<string>(GAME_SOUND_KEYS);
    for (const [key, path] of Object.entries(RETTE_ANNA_ASSETS.audio)) {
      const custom = customKeys.has(key) ? customSounds[key as GameSoundKey] : undefined;
      loader.audio(key, custom ? custom.dataUrl : path);
    }
    // Charakter-Sprites: jede ID landet als eigene Textur.
    for (const entry of characterAssetEntries()) {
      loader.image(entry.key, entry.path);
    }

    loader.on("loaderror", (file: Phaser.Loader.File) => {
      // Wir loggen den Fehler nur und arbeiten unten mit Platzhaltern weiter.
      console.warn(`[rette-anna] Asset fehlt: ${file.key} (${file.url})`);
    });

    // Lade-Bar
    const { width, height } = this.scale;
    const barBg = this.add.rectangle(width / 2, height / 2, 320, 14, 0x2a2f4a);
    const bar = this.add.rectangle(width / 2 - 160, height / 2, 0, 14, 0x7c3aed)
      .setOrigin(0, 0.5);
    const label = this.add
      .text(width / 2, height / 2 - 28, "Lade Princess in Danger …", {
        fontFamily: "Inter, sans-serif",
        fontSize: "18px",
        color: "#e7e9f4",
      })
      .setOrigin(0.5);

    loader.on("progress", (value: number) => {
      bar.width = 320 * value;
    });
    loader.on("complete", () => {
      barBg.destroy();
      bar.destroy();
      label.destroy();
    });
  }

  create() {
    this.ensurePlaceholderTextures();
    // iOS-Audio entsperren, falls noch nicht passiert. Der globale Listener
    // in App.tsx fängt das beim ersten Tap ab; hier rufen wir es zusätzlich
    // auf, weil der „Spiel starten"-Klick mit Glück bis hier her als Geste
    // gilt – und damit auch Phasers eigener Sound-Manager aufwacht.
    unlockAudioContexts();
    const soundMgr = this.sound as unknown as {
      context?: AudioContext;
      unlock?: () => void;
    };
    try {
      if (soundMgr.context && soundMgr.context.state === "suspended") {
        void soundMgr.context.resume();
      }
      soundMgr.unlock?.();
    } catch {
      /* nicht kritisch */
    }
    this.scene.start("PlayScene", { ctx: this.ctx });
    this.scene.launch("UIScene", { ctx: this.ctx });
  }

  /**
   * Erstellt mit dem Phaser-Graphics-API einfache Platzhalter-Texturen, falls
   * die echten Bilder noch nicht im public-Ordner liegen.
   */
  private ensurePlaceholderTextures() {
    // Charakter-Platzhalter pro `style` aus dem Shared-Katalog
    for (const character of CHARACTERS) {
      const key = characterSpriteKey(character.id);
      if (this.textures.exists(key)) continue;
      const color = Phaser.Display.Color.HexStringToColor(character.color).color;
      const accent = Phaser.Display.Color.HexStringToColor(character.accent).color;
      const gfx = this.add.graphics({ x: 0, y: 0 });
      drawCharacterSprite(gfx, character.style, color, accent, key);
      gfx.destroy();
    }

    const generated: Array<[string, (g: Phaser.GameObjects.Graphics) => void, number, number]> = [
      [
        "anna",
        (g) => {
          g.fillStyle(0xfbcfe8, 1).fillRoundedRect(0, 16, 36, 40, 8);
          g.fillStyle(0xec4899, 1).fillTriangle(0, 16, 36, 16, 18, 0); // crown
          g.fillStyle(0x1a1530, 1).fillCircle(12, 28, 2).fillCircle(24, 28, 2);
          g.fillStyle(0xec4899, 1).fillRect(14, 36, 8, 2);
        },
        36,
        56,
      ],
      [
        "monster",
        (g) => {
          // Boden-Monster mit Hörnchen
          g.fillStyle(0x10b981, 1).fillRoundedRect(0, 6, 40, 30, 8);
          g.fillStyle(0x059669, 1).fillTriangle(4, 6, 12, 6, 8, 0);
          g.fillStyle(0x059669, 1).fillTriangle(28, 6, 36, 6, 32, 0);
          g.fillStyle(0xffffff, 1).fillCircle(13, 16, 5).fillCircle(27, 16, 5);
          g.fillStyle(0x1a1530, 1).fillCircle(13, 16, 2).fillCircle(27, 16, 2);
          g.fillStyle(0x1a1530, 1).fillRect(10, 26, 20, 4);
          g.fillStyle(0xffffff, 1).fillTriangle(10, 26, 14, 26, 12, 30);
          g.fillStyle(0xffffff, 1).fillTriangle(26, 26, 30, 26, 28, 30);
        },
        40,
        36,
      ],
      [
        "flying-monster",
        (g) => {
          // Drache/Fledermaus mit Flügeln
          g.fillStyle(0x991b1b, 1).fillTriangle(0, 16, 14, 8, 14, 24);
          g.fillStyle(0x991b1b, 1).fillTriangle(36, 8, 50, 16, 36, 24);
          g.fillStyle(0xdc2626, 1).fillEllipse(25, 16, 26, 20);
          g.fillStyle(0x7f1d1d, 1).fillTriangle(18, 8, 22, 2, 22, 8);
          g.fillStyle(0x7f1d1d, 1).fillTriangle(28, 8, 28, 2, 32, 8);
          g.fillStyle(0xfde047, 1).fillCircle(21, 16, 3).fillCircle(29, 16, 3);
          g.fillStyle(0x0b0d17, 1).fillCircle(21, 16, 1.5).fillCircle(29, 16, 1.5);
          // Fies-Zähne
          g.fillStyle(0xffffff, 1).fillTriangle(22, 22, 24, 22, 23, 26);
          g.fillStyle(0xffffff, 1).fillTriangle(26, 22, 28, 22, 27, 26);
        },
        50,
        32,
      ],
      [
        "spike",
        (g) => {
          // Spitzes, metallisches Dreieck mit Schraffur
          g.fillStyle(0x7f1d1d, 1).fillTriangle(0, 24, 12, 0, 24, 24);
          g.fillStyle(0xef4444, 1).fillTriangle(2, 24, 12, 4, 22, 24);
          g.lineStyle(1, 0xfecaca, 0.8);
          g.beginPath();
          g.moveTo(12, 4);
          g.lineTo(12, 24);
          g.strokePath();
          g.fillStyle(0xfecaca, 1).fillTriangle(10, 8, 12, 4, 14, 8);
        },
        24,
        24,
      ],
      [
        "ground-trap",
        (g) => {
          // Bodenfalle: dunkler Metall-Sockel mit Nieten + drei silbrige Spikes
          g.fillStyle(0x1c1917, 1).fillRect(0, 20, 56, 14);
          g.fillStyle(0x44403c, 1).fillRect(0, 20, 56, 3);
          g.lineStyle(1.5, 0x57534e, 1).strokeRect(0, 20, 56, 14);
          // Nieten
          g.fillStyle(0x78716c, 1)
            .fillCircle(4, 27, 1.4)
            .fillCircle(28, 27, 1.4)
            .fillCircle(52, 27, 1.4);
          // Spikes (silbrig-metallisch, eindeutig anders als Boden-Spikes)
          g.fillStyle(0x57534e, 1)
            .fillTriangle(4, 20, 12, 2, 20, 20)
            .fillTriangle(20, 20, 28, 2, 36, 20)
            .fillTriangle(36, 20, 44, 2, 52, 20);
          g.fillStyle(0xa8a29e, 1)
            .fillTriangle(7, 20, 12, 6, 17, 20)
            .fillTriangle(23, 20, 28, 6, 33, 20)
            .fillTriangle(39, 20, 44, 6, 49, 20);
          g.fillStyle(0xe7e5e4, 0.9)
            .fillTriangle(11, 16, 12, 6, 13, 16)
            .fillTriangle(27, 16, 28, 6, 29, 16)
            .fillTriangle(43, 16, 44, 6, 45, 16);
        },
        56,
        36,
      ],
      [
        "platform",
        (g) => {
          g.fillStyle(0x1f2547, 1).fillRect(0, 0, 64, 32);
          g.fillStyle(0x2a3060, 1).fillRect(0, 0, 64, 6);
        },
        64,
        32,
      ],
      [
        "lava-tile",
        (g) => {
          // Animierte Lava-Optik (statisch, aber Schichten)
          g.fillStyle(0x7f1d1d, 1).fillRect(0, 0, 64, 32);
          g.fillStyle(0xf97316, 1).fillRect(0, 4, 64, 22);
          g.fillStyle(0xfbbf24, 1).fillRect(0, 8, 64, 12);
          g.fillStyle(0xfde047, 0.7);
          for (let i = 0; i < 4; i += 1) {
            g.fillCircle(8 + i * 18, 14, 3);
          }
        },
        64,
        32,
      ],
      [
        "bullet",
        (g) => {
          g.fillStyle(0xfde047, 1).fillCircle(8, 8, 7);
          g.fillStyle(0xfbbf24, 1).fillCircle(8, 8, 5);
          g.fillStyle(0xfff7d6, 1).fillCircle(6, 6, 2);
        },
        16,
        16,
      ],
      [
        "fireball",
        (g) => {
          // Größerer, fieserer Feuerball
          g.fillStyle(0x7f1d1d, 1).fillCircle(12, 12, 11);
          g.fillStyle(0xf97316, 1).fillCircle(12, 12, 9);
          g.fillStyle(0xfde047, 1).fillCircle(11, 11, 5);
          g.fillStyle(0xfff7d6, 1).fillCircle(9, 9, 2);
        },
        24,
        24,
      ],
      [
        "background",
        (g) => {
          // Sanfter Sky-Gradient
          g.fillGradientStyle(0x0a0a1f, 0x0a0a1f, 0x1c1844, 0x1c1844, 1);
          g.fillRect(0, 0, 1280, 720);
          // Sterne in zwei Größen für etwas Tiefe – aber keine großen
          // grauen Nebel-Kreise mehr (die wirkten wie viele Monde).
          g.fillStyle(0xffffff, 0.9);
          for (let i = 0; i < 90; i += 1) {
            const x = Math.floor(Math.random() * 1280);
            const y = Math.floor(Math.random() * 520);
            g.fillCircle(x, y, Math.random() * 1.1 + 0.4);
          }
          g.fillStyle(0xffffff, 0.4);
          for (let i = 0; i < 40; i += 1) {
            const x = Math.floor(Math.random() * 1280);
            const y = Math.floor(Math.random() * 480);
            g.fillCircle(x, y, Math.random() * 0.7 + 0.3);
          }
          // Ein einzelner Mond als ruhiger Anker – ohne Krater, sonst sah's
          // aus, als hätte der Mond Begleiter.
          g.fillStyle(0xfde68a, 0.9).fillCircle(220, 140, 30);
          g.fillStyle(0xfff7d6, 0.5).fillCircle(212, 132, 12);
        },
        1280,
        720,
      ],
    ];

    for (const [key, draw, w, h] of generated) {
      if (this.textures.exists(key)) continue;
      const gfx = this.add.graphics({ x: 0, y: 0 });
      draw(gfx);
      gfx.generateTexture(key, w, h);
      gfx.destroy();
    }

    // Pro Charakter-Style ein eigenes Projektil. Wenn der Style schon
    // einmal verarbeitet wurde, überspringen.
    const styles = new Set(CHARACTERS.map((c) => c.style));
    for (const style of styles) {
      const key = `bullet-${style}`;
      if (this.textures.exists(key)) continue;
      const gfx = this.add.graphics({ x: 0, y: 0 });
      const size = drawBulletSprite(gfx, style, key);
      gfx.destroy();
      void size;
    }
  }
}

/**
 * Zeichnet einen Charakter in die Graphics-Instanz und erzeugt eine
 * Phaser-Textur unter `key`. Pro `style`-Wert gibt es eine eigene Optik.
 */
function drawCharacterSprite(
  gfx: Phaser.GameObjects.Graphics,
  style: string,
  color: number,
  accent: number,
  key: string,
) {
  if (style === "stitch") {
    gfx.fillStyle(color, 1);
    gfx.fillEllipse(8, 12, 12, 18);
    gfx.fillEllipse(36, 12, 12, 18);
    gfx.fillStyle(accent, 1);
    gfx.fillEllipse(8, 14, 6, 10);
    gfx.fillEllipse(36, 14, 6, 10);
    gfx.fillStyle(color, 1).fillRoundedRect(4, 14, 36, 36, 12);
    gfx.fillStyle(accent, 0.45).fillEllipse(22, 38, 22, 18);
    gfx.fillStyle(0x0b0d17, 1).fillCircle(15, 26, 4).fillCircle(29, 26, 4);
    gfx.fillStyle(0xffffff, 1).fillCircle(16, 25, 1.4).fillCircle(30, 25, 1.4);
    gfx.fillStyle(0x0b0d17, 1).fillEllipse(22, 32, 4, 3);
    gfx.lineStyle(1.4, 0x0b0d17, 1);
    gfx.strokeLineShape(new Phaser.Geom.Line(18, 36, 22, 39));
    gfx.strokeLineShape(new Phaser.Geom.Line(22, 39, 26, 36));
    gfx.generateTexture(key, 44, 52);
    return;
  }
  if (style === "lion") {
    // Mähne (Akzent)
    gfx.fillStyle(accent, 1).fillCircle(22, 26, 22);
    gfx.fillStyle(color, 1).fillEllipse(22, 28, 28, 26);
    // Ohren
    gfx.fillStyle(accent, 1).fillCircle(10, 14, 5).fillCircle(34, 14, 5);
    gfx.fillStyle(color, 1).fillCircle(10, 14, 2.5).fillCircle(34, 14, 2.5);
    // Schnauze
    gfx.fillStyle(0xfde68a, 1).fillEllipse(22, 34, 16, 10);
    // Augen
    gfx.fillStyle(0x0b0d17, 1).fillCircle(17, 26, 2).fillCircle(27, 26, 2);
    // Nase + Mund
    gfx.fillStyle(0x0b0d17, 1).fillTriangle(20, 32, 24, 32, 22, 35);
    gfx.lineStyle(1.3, 0x0b0d17, 1);
    gfx.strokeLineShape(new Phaser.Geom.Line(22, 35, 22, 38));
    gfx.generateTexture(key, 44, 52);
    return;
  }
  if (style === "snowman") {
    // Drei Bälle
    gfx.fillStyle(color, 1).fillCircle(18, 44, 10);
    gfx.fillStyle(color, 1).fillCircle(18, 28, 9);
    gfx.fillStyle(color, 1).fillCircle(18, 14, 7);
    // Augen + Kohlemund
    gfx.fillStyle(0x0b0d17, 1).fillCircle(15, 12, 1.4).fillCircle(21, 12, 1.4);
    gfx.fillCircle(15, 18, 1).fillCircle(18, 18.5, 1).fillCircle(21, 18, 1);
    // Karottennase
    gfx.fillStyle(accent, 1).fillTriangle(18, 14, 28, 16, 18, 17);
    // Knöpfe
    gfx.fillStyle(0x0b0d17, 1).fillCircle(18, 26, 1.2).fillCircle(18, 42, 1.4).fillCircle(18, 48, 1.4);
    // Ast-Arme
    gfx.lineStyle(1.5, 0x7c2d12, 1);
    gfx.strokeLineShape(new Phaser.Geom.Line(8, 28, 0, 22));
    gfx.strokeLineShape(new Phaser.Geom.Line(28, 28, 36, 22));
    gfx.generateTexture(key, 36, 56);
    return;
  }
  if (style === "mouse") {
    // Ohren (groß und rund)
    gfx.fillStyle(color, 1).fillCircle(8, 8, 8).fillCircle(36, 8, 8);
    // Kopf
    gfx.fillStyle(color, 1).fillCircle(22, 26, 14);
    // Heller Gesichtsbereich
    gfx.fillStyle(0xfde68a, 1).fillEllipse(22, 32, 18, 12);
    // Augen
    gfx.fillStyle(0xffffff, 1).fillEllipse(17, 24, 4, 6).fillEllipse(27, 24, 4, 6);
    gfx.fillStyle(0x0b0d17, 1).fillEllipse(17, 25, 2, 3).fillEllipse(27, 25, 2, 3);
    // Nase
    gfx.fillStyle(0x0b0d17, 1).fillEllipse(22, 30, 5, 4);
    // Körper (rote Hose mit Knöpfen)
    gfx.fillStyle(accent, 1).fillRoundedRect(10, 38, 24, 14, 4);
    gfx.fillStyle(0xfde68a, 1).fillCircle(16, 44, 1.2).fillCircle(28, 44, 1.2);
    gfx.generateTexture(key, 44, 56);
    return;
  }
  if (style === "genie") {
    // Rauch-Basis
    gfx.fillStyle(color, 0.4).fillEllipse(18, 52, 30, 8);
    // Körper (tropfenförmig)
    gfx.fillStyle(color, 1);
    gfx.beginPath();
    gfx.moveTo(8, 50);
    gfx.lineTo(8, 28);
    gfx.lineTo(18, 18);
    gfx.lineTo(28, 28);
    gfx.lineTo(28, 50);
    gfx.closePath();
    gfx.fillPath();
    // Kopf
    gfx.fillStyle(color, 1).fillCircle(18, 18, 8);
    // Turban
    gfx.fillStyle(accent, 1);
    gfx.beginPath();
    gfx.moveTo(10, 14);
    gfx.lineTo(26, 14);
    gfx.lineTo(22, 8);
    gfx.lineTo(14, 8);
    gfx.closePath();
    gfx.fillPath();
    gfx.fillStyle(0x7f1d1d, 1).fillCircle(18, 10, 1.4);
    // Augen
    gfx.fillStyle(0x0b0d17, 1).fillCircle(15, 19, 1.4).fillCircle(21, 19, 1.4);
    // Lächeln
    gfx.lineStyle(1.3, 0x0b0d17, 1);
    gfx.strokeLineShape(new Phaser.Geom.Line(15, 23, 21, 23));
    gfx.generateTexture(key, 36, 56);
    return;
  }
  if (style === "spaceman") {
    // Helm
    gfx.fillStyle(color, 1).fillRoundedRect(2, 6, 32, 28, 10);
    // Visier
    gfx.fillStyle(0x1e1b4b, 1).fillRoundedRect(7, 12, 22, 12, 5);
    gfx.fillStyle(0x60a5fa, 0.6).fillRect(9, 14, 6, 3);
    // Antenne
    gfx.fillStyle(accent, 1).fillRect(9, 2, 1.5, 6).fillCircle(9.5, 2, 1.5);
    // Körper
    gfx.fillStyle(color, 1).fillRoundedRect(6, 32, 24, 20, 4);
    gfx.fillStyle(accent, 1).fillCircle(12, 42, 1.4).fillCircle(24, 42, 1.4);
    gfx.fillStyle(0xef4444, 1).fillCircle(18, 42, 1.4);
    gfx.fillStyle(accent, 1).fillRect(6, 46, 24, 2);
    gfx.generateTexture(key, 36, 56);
    return;
  }
  // Standard-Held ("human")
  gfx.fillStyle(color, 1).fillRoundedRect(0, 0, 36, 52, 8);
  gfx.fillStyle(accent, 0.35).fillRoundedRect(6, 30, 24, 18, 6);
  gfx.fillStyle(0xffffff, 1).fillCircle(12, 18, 4).fillCircle(24, 18, 4);
  gfx.fillStyle(0x0b0d17, 1).fillCircle(12, 18, 2).fillCircle(24, 18, 2);
  gfx.fillStyle(0x0b0d17, 1).fillRect(14, 36, 8, 2);
  gfx.generateTexture(key, 36, 52);
}

/**
 * Zeichnet das Charakter-spezifische Projektil und erzeugt eine Phaser-Textur
 * unter `key`. Liefert die Texturgröße `{ w, h }` zurück, damit Aufrufer
 * sie für die Physics-Body-Größe verwenden könnten.
 */
function drawBulletSprite(
  gfx: Phaser.GameObjects.Graphics,
  style: string,
  key: string,
): { w: number; h: number } {
  switch (style) {
    case "snowman": {
      // Olaf – Schneeball mit hellblauen Schatten
      const w = 20;
      const h = 20;
      gfx.fillStyle(0xe0f2fe, 1).fillCircle(w / 2, h / 2, 9);
      gfx.fillStyle(0xffffff, 1).fillCircle(w / 2, h / 2, 7);
      gfx.fillStyle(0xbae6fd, 0.7).fillCircle(7, 13, 2.2);
      gfx.fillStyle(0xbae6fd, 0.7).fillCircle(13, 7, 1.6);
      gfx.fillStyle(0xffffff, 1).fillCircle(7, 7, 2);
      gfx.generateTexture(key, w, h);
      return { w, h };
    }
    case "lion": {
      // Simba – goldener Feuerring (Brüll-Welle)
      const w = 22;
      const h = 22;
      gfx.fillStyle(0xfb923c, 0.85).fillCircle(w / 2, h / 2, 11);
      gfx.fillStyle(0xf59e0b, 1).fillCircle(w / 2, h / 2, 8);
      gfx.fillStyle(0xfde047, 1).fillCircle(w / 2, h / 2, 5);
      gfx.fillStyle(0x7c2d12, 1).fillCircle(w / 2, h / 2, 2.5);
      // Funken außen
      gfx.fillStyle(0xfde047, 0.9);
      gfx.fillTriangle(w / 2, 0, w / 2 - 2, 4, w / 2 + 2, 4);
      gfx.fillTriangle(w / 2, h, w / 2 - 2, h - 4, w / 2 + 2, h - 4);
      gfx.fillTriangle(0, h / 2, 4, h / 2 - 2, 4, h / 2 + 2);
      gfx.fillTriangle(w, h / 2, w - 4, h / 2 - 2, w - 4, h / 2 + 2);
      gfx.generateTexture(key, w, h);
      return { w, h };
    }
    case "stitch": {
      // Stitch – blauer Plasma-Spit
      const w = 20;
      const h = 18;
      gfx.fillStyle(0x6366f1, 0.6).fillEllipse(w / 2, h / 2, 18, 12);
      gfx.fillStyle(0x3b82f6, 1).fillEllipse(w / 2, h / 2, 14, 9);
      gfx.fillStyle(0x93c5fd, 1).fillEllipse(w / 2 - 1, h / 2 - 1, 8, 5);
      gfx.fillStyle(0xffffff, 1).fillCircle(w / 2 - 2, h / 2 - 1, 1.5);
      gfx.generateTexture(key, w, h);
      return { w, h };
    }
    case "mouse": {
      // Mickey – roter 5-zackiger Stern
      const w = 20;
      const h = 20;
      const cx = w / 2;
      const cy = h / 2;
      const r1 = 9;
      const r2 = 4;
      const points: number[] = [];
      for (let i = 0; i < 10; i += 1) {
        const r = i % 2 === 0 ? r1 : r2;
        const a = (Math.PI / 5) * i - Math.PI / 2;
        points.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      }
      gfx.fillStyle(0xb91c1c, 1).beginPath();
      gfx.moveTo(points[0]!, points[1]!);
      for (let i = 2; i < points.length; i += 2) {
        gfx.lineTo(points[i]!, points[i + 1]!);
      }
      gfx.closePath();
      gfx.fillPath();
      gfx.fillStyle(0xfde047, 1).fillCircle(cx, cy, 2.4);
      gfx.generateTexture(key, w, h);
      return { w, h };
    }
    case "genie": {
      // Genie – magischer blau-goldener Funke
      const w = 20;
      const h = 20;
      gfx.fillStyle(0x3b82f6, 0.55).fillCircle(w / 2, h / 2, 9);
      gfx.fillStyle(0x60a5fa, 1).fillCircle(w / 2, h / 2, 6);
      gfx.fillStyle(0xfde047, 1).fillCircle(w / 2, h / 2, 3);
      // Glitzer
      gfx.fillStyle(0xfff7d6, 1)
        .fillCircle(3, 4, 1.2)
        .fillCircle(17, 5, 1)
        .fillCircle(15, 16, 1.2)
        .fillCircle(4, 15, 1);
      gfx.generateTexture(key, w, h);
      return { w, h };
    }
    case "spaceman": {
      // Buzz – horizontaler grün-cyaner Laser
      const w = 26;
      const h = 10;
      gfx.fillStyle(0x064e3b, 0.6).fillRoundedRect(0, 2, w, 6, 3);
      gfx.fillStyle(0x22c55e, 1).fillRoundedRect(1, 3, w - 2, 4, 2);
      gfx.fillStyle(0xa7f3d0, 1).fillRoundedRect(3, 4, w - 6, 2, 1);
      gfx.fillStyle(0xffffff, 1).fillCircle(w - 4, 5, 2);
      gfx.generateTexture(key, w, h);
      return { w, h };
    }
    // human + alles andere: Standard-Kugel (mit Beibehaltung der "bullet"-Optik)
    default: {
      const w = 16;
      const h = 16;
      gfx.fillStyle(0xfde047, 1).fillCircle(8, 8, 7);
      gfx.fillStyle(0xfbbf24, 1).fillCircle(8, 8, 5);
      gfx.fillStyle(0xfff7d6, 1).fillCircle(6, 6, 2);
      gfx.generateTexture(key, w, h);
      return { w, h };
    }
  }
}
